import type {
	PageIncludeMacroParams,
	PageIncludeQueryTarget,
	ParsedPageIncludeTarget,
} from '../pageIncludeMacroTypes.ts';

/**
 * Parses macro parameters to extract the include target location.
 *
 * Resolution priority (matches backend behavior):
 * 1. Modern typed default parameter `""` (confluence-content typed link)
 * 2. Legacy `pageTitle` parameter (takes precedence over `"0"`)
 * 3. `spaceKey` + `pageTitle` joined as "SPACEKEY:Page Title"
 * 4. Legacy untyped default parameter `"0"`
 *
 * Returns a parsed location string or an error if no target can be determined.
 */
export const parseMacroTarget = (
	macroParams: PageIncludeMacroParams | undefined,
): ParsedPageIncludeTarget => {
	const referencedContentId = macroParams?.['_referencedContentId']?.value ?? null;
	const parentId = macroParams?.['_parentId']?.value ?? null;

	// 1. Modern typed parameter (key is "")
	const typedValue = macroParams?.['']?.value?.trim();
	if (typedValue) {
		return { location: typedValue, referencedContentId, parentId, error: null };
	}

	// 2. Legacy pageTitle (takes precedence over "0")
	const pageTitle = macroParams?.['pageTitle']?.value?.trim();
	if (pageTitle) {
		const spaceKey = macroParams?.['spaceKey']?.value?.trim();
		const location = spaceKey ? `${spaceKey}:${pageTitle}` : pageTitle;
		return { location, referencedContentId, parentId, error: null };
	}

	// 3. Legacy untyped default parameter "0"
	const legacyDefault = macroParams?.['0']?.value?.trim();
	if (legacyDefault) {
		return { location: legacyDefault, referencedContentId, parentId, error: null };
	}

	// No target configured
	return { location: null, referencedContentId, parentId, error: 'missing-target' };
};

const extractContentIdFromTypedValue = (value: string): string | null => {
	const match = value.match(/confluence-content:\/\/[^/]+\/(\d+)/);
	return match ? match[1] : null;
};

const parseLegacyLocation = (location: string): { spaceKey: string | null; title: string } => {
	const colonIdx = location.indexOf(':');
	if (colonIdx > 0) {
		return {
			spaceKey: location.slice(0, colonIdx).trim(),
			title: location.slice(colonIdx + 1).trim(),
		};
	}
	return { spaceKey: null, title: location.trim() };
};

/**
 * Converts a parsed include target into the concrete GraphQL query target.
 * Kept pure so the same logic can be reused by React hooks and future SSR query execution.
 */
export const getPageIncludeQueryTarget = (
	parsedTarget: ParsedPageIncludeTarget,
	hostSpaceKey: string | undefined,
): PageIncludeQueryTarget => {
	if (!parsedTarget.location) {
		return { kind: 'missing-target' };
	}

	const idFromTyped = extractContentIdFromTypedValue(parsedTarget.location);
	if (idFromTyped) {
		return { kind: 'by-id', variables: { contentId: idFromTyped } };
	}

	if (parsedTarget.referencedContentId) {
		return { kind: 'by-id', variables: { contentId: parsedTarget.referencedContentId } };
	}

	const { spaceKey, title } = parseLegacyLocation(parsedTarget.location);
	const querySpaceKey = spaceKey ?? hostSpaceKey;

	if (!title || !querySpaceKey) {
		return { kind: 'missing-target' };
	}

	return { kind: 'by-title', variables: { spaceKey: querySpaceKey, title } };
};
